import React, { useEffect, useMemo, useState } from 'react';
import { Table } from 'syngenta-digital-cropwise-react-ui-kit';
import useTablePaginationAndSort from 'hooks/useTablePaginationAndSort';
import { getAbbreviatedUnit } from 'helpers/unitsAbbreviation';
import formatNumber from 'helpers/formatNumber';
import getInvoiceCostVisibility from 'screens/Invoices/helpers/getInvoiceCostVisibility';
import { getHeaderSortClassName } from 'helpers/tableSort';
import useTableScrollConfig from 'screens/Applications&WorkOrders/Components/DetailsTabs/hooks/useTableScrollConfig';
import { OPERATION_DETAILS_TABS_WRAPPER_SELECTOR } from 'screens/Applications&WorkOrders/Components/DetailsTabs/constants';
import useProduct from 'hooks/useProduct';
import resolveUnitPrice, { LineUnitPrice } from '../helpers/resolveUnitPrice';
import { InvoiceProductDetail } from '../types';

const NOT_AVAILABLE = '---';

const lineUnitPrice = (product: InvoiceProductDetail): LineUnitPrice | null =>
  resolveUnitPrice({
    storedValue: product.unitPriceValue,
    storedUnit: product.unitPriceUnit,
    quantityValue: product.totalQuantityValue,
    quantityUnit: product.totalQuantityUnit,
    costValue: product.totalCostValue
  });

const unitPriceOf = (product: InvoiceProductDetail): number =>
  lineUnitPrice(product)?.value ?? 0;

const PAGE_SIZE = 20;
const INVOICE_PRODUCTS_TABLE_SCROLL_X = 500;
const INVOICE_PRODUCTS_TABLE_OFFSET_EM = 39.75;
// Matches the Applications ProductsTab's no-tank-info leading height, so the
// shared hook's fixed chrome-reserve constants (calibrated against that tab)
// budget the same headroom here (FR-090).
const INVOICE_PRODUCTS_TABLE_LEADING_HEIGHT = 8;
// The invoice detail panel's content area uses `pb-8` (32px) below the tabs,
// so the table must stop 32px above the page bottom. Without this the shared
// hook defaults to a 16px reserve and the table visibly grows into that extra
// 16px after mount.
const INVOICE_PANEL_BOTTOM_PADDING_PX = 32;

export interface InvoiceProductsTabProps {
  products: InvoiceProductDetail[];
  memberRole?: string;
  memberFinancialAccess?: string;
  /** False while another detail tab is showing (this pane stays mounted). */
  isActiveTab?: boolean;
}

/**
 * Products tab body for the invoice detail panel. Extracted from
 * `InvoiceDetailPanel` so that `useTableScrollConfig` (and its one-shot
 * `useLayoutEffect` measurement of `.operation-details-tabs`) only runs once
 * this subtree is actually mounted — i.e. when an invoice is selected and the
 * tabs are in the DOM. Mirrors the Applications & Work Orders `ProductsTab`,
 * whose hook likewise measures on mount-when-selected. Keeping the hook at the
 * always-mounted `InvoiceDetailPanel` level measured the empty-state DOM (no
 * `.operation-details-tabs`), so it stuck on the viewport fallback (~467px) and
 * the table never grew to fill the page (FR-090).
 */
const InvoiceProductsTab: React.FC<InvoiceProductsTabProps> = ({
  products,
  memberRole,
  memberFinancialAccess,
  isActiveTab = true
}) => {
  const maskCost = (value: number | null | undefined): string =>
    getInvoiceCostVisibility({
      cost: value ?? 0,
      memberRole,
      memberFinancialAccess
    }) || '';

  const { fetchProductData } = useProduct();
  const [manufacturerByProductId, setManufacturerByProductId] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    const missing = products.filter(
      p => p.productId && !Object.hasOwn(manufacturerByProductId, p.productId)
    );
    if (!missing.length) return undefined;

    let cancelled = false;

    Promise.all(
      missing.map(p =>
        fetchProductData({ productId: p.productId, custom: p.custom }).then(
          (result: { data: { manufacturer?: string } } | null) => ({
            productId: p.productId as string,
            // Cache each productId at most once per mount even when the
            // lookup has no manufacturer, so it isn't re-fetched on every
            // subsequent `products` change (sort/pagination/refresh).
            manufacturer: result?.data?.manufacturer ?? ''
          })
        )
      )
    ).then(results => {
      if (cancelled) return;
      const additions: Record<string, string> = {};
      results.forEach(({ productId, manufacturer }) => {
        additions[productId] = manufacturer;
      });
      setManufacturerByProductId(prev => ({ ...prev, ...additions }));
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  const {
    reactUiKitTablePaginationProp,
    reactUiKitTableOnChange,
    sortBy,
    sortDir,
    page
  } = useTablePaginationAndSort({ pageSize: PAGE_SIZE }) as any;

  const productRowCountOnPage = useMemo(() => {
    if (products.length === 0) return 0;
    const start = (page - 1) * PAGE_SIZE;
    return Math.min(Math.max(products.length - start, 0), PAGE_SIZE);
  }, [products.length, page]);

  const hasPaginationNavigation = products.length > PAGE_SIZE;

  const productColumns = useMemo(() => {
    // The detail tabs keep this pane mounted (forceRender) and just hide it, so
    // a sorter tooltip left open by a header click would survive a switch to
    // another tab; force it closed while the pane is hidden. `true` restores
    // the default hover tooltip once the tab is active again.
    const sorterTooltip = isActiveTab ? true : { open: false };

    return [
      {
        title: 'Product Name',
        dataIndex: 'productName',
        key: 'productName',
        width: 200,
        fixed: true,
        className: getHeaderSortClassName('productName', sortBy, sortDir),
        showSorterTooltip: sorterTooltip,
        sorter: (a: InvoiceProductDetail, b: InvoiceProductDetail) =>
          a.productName.localeCompare(b.productName),
        sortOrder: sortBy === 'productName' && sortDir,
        render: (_: any, record: InvoiceProductDetail) => {
          const manufacturer =
            record.productId && manufacturerByProductId[record.productId];
          return (
            <>
              <div className="text-neutral-90 font-medium truncate">
                {record.productName}
              </div>
              {manufacturer && (
                <div className="text-neutral-60 text-xs truncate">
                  {manufacturer}
                </div>
              )}
            </>
          );
        }
      },
      {
        title: (
          <span className="block w-full text-right">
            Total Invoiced Quantity
          </span>
        ),
        dataIndex: 'totalProduct',
        key: 'totalProduct',
        width: 150,
        className: `justify-end text-right ${getHeaderSortClassName(
          'totalProduct',
          sortBy,
          sortDir
        )}`,
        showSorterTooltip: sorterTooltip,
        sorter: (a: InvoiceProductDetail, b: InvoiceProductDetail) =>
          a.totalQuantityValue - b.totalQuantityValue,
        sortOrder: sortBy === 'totalProduct' && sortDir,
        render: (_: any, record: InvoiceProductDetail) => (
          <div className="text-dark-grey font-medium text-right">
            {formatNumber(
              record.totalQuantityValue,
              getAbbreviatedUnit(record.totalQuantityUnit)
            )}
          </div>
        )
      },
      {
        title: <span className="block w-full text-right">Unit Price</span>,
        dataIndex: 'unitPrice',
        key: 'unitPrice',
        width: 150,
        className: `justify-end text-right ${getHeaderSortClassName(
          'unitPrice',
          sortBy,
          sortDir
        )}`,
        showSorterTooltip: sorterTooltip,
        sorter: (a: InvoiceProductDetail, b: InvoiceProductDetail) =>
          unitPriceOf(a) - unitPriceOf(b),
        sortOrder: sortBy === 'unitPrice' && sortDir,
        render: (_: any, record: InvoiceProductDetail) => {
          const price = lineUnitPrice(record);
          const masked = maskCost(price?.value ?? 0);
          const unitSuffix = getAbbreviatedUnit(
            price?.unit ?? record.totalQuantityUnit
          );
          return (
            <div className="text-dark-grey font-medium text-right">
              {masked ? `${masked}/${unitSuffix}` : NOT_AVAILABLE}
            </div>
          );
        }
      },
      {
        title: 'Total Cost',
        dataIndex: 'totalCost',
        key: 'totalCost',
        width: 180,
        className: `justify-end text-right ${getHeaderSortClassName(
          'totalCost',
          sortBy,
          sortDir
        )}`,
        showSorterTooltip: sorterTooltip,
        sorter: (a: InvoiceProductDetail, b: InvoiceProductDetail) =>
          (a.totalCostValue ?? 0) - (b.totalCostValue ?? 0),
        sortOrder: sortBy === 'totalCost' && sortDir,
        render: (_: any, record: InvoiceProductDetail) => (
          <div className="text-dark-grey font-medium text-right">
            {maskCost(record.totalCostValue) || NOT_AVAILABLE}
          </div>
        )
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sortBy,
    sortDir,
    memberRole,
    memberFinancialAccess,
    manufacturerByProductId,
    isActiveTab
  ]);

  const tableScrollConfig = useTableScrollConfig({
    x: INVOICE_PRODUCTS_TABLE_SCROLL_X,
    rowCount: productRowCountOnPage,
    desktopOffsetEm: INVOICE_PRODUCTS_TABLE_OFFSET_EM,
    leadingHeight: INVOICE_PRODUCTS_TABLE_LEADING_HEIGHT,
    wrapperSelector: OPERATION_DETAILS_TABS_WRAPPER_SELECTOR,
    containerBottomPaddingPx: INVOICE_PANEL_BOTTOM_PADDING_PX
  });

  return (
    <div className="px-6 py-8" data-testid="invoice-products">
      <div
        className={`application-view-products-tab-table invoice-products-table-wrapper${
          hasPaginationNavigation
            ? ''
            : ' application-view-products-tab-table-no-pagination'
        }`}
      >
        <Table
          columns={productColumns}
          scroll={tableScrollConfig}
          pagination={reactUiKitTablePaginationProp}
          onChange={reactUiKitTableOnChange}
          dataSource={products}
          locale={{
            emptyText: (
              <span className="text-sm text-neutral-60">{NOT_AVAILABLE}</span>
            )
          }}
        />
      </div>
    </div>
  );
};

export default InvoiceProductsTab;
